import { CognitoIdentityProviderClient } from '@aws-sdk/client-cognito-identity-provider';
import { APP_URL, AWS_COGNITO_CLIENT_ID, AWS_COGNITO_DOMAIN, AWS_REGION, PKCE_CODE_VERIFIER_KEY } from '@/utils/constants';

export const cognitoClient = new CognitoIdentityProviderClient({
  region: AWS_REGION,
});

/**
 * Generates a cryptographically random string suitable for a PKCE code_verifier.
 * The length will be between 43 and 128 characters, as per RFC 7636.
 *
 * @returns {string} The generated code_verifier.
 */
function generateCodeVerifier(): string {
  // eslint-disable-next-line no-secrets/no-secrets
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const randomBytes = new Uint8Array(128); // Max length for code_verifier
  window.crypto.getRandomValues(randomBytes); // Fill with random values

  let codeVerifier = '';
  // Ensure the length is between 43 and 128 characters.
  // We'll aim for 128 and then trim if needed, or just use a subset.
  // A simple approach is to map random bytes to the possible characters.
  for (let i = 0; i < randomBytes.length; i++) {
    codeVerifier += possible[randomBytes[i] % possible.length];
  }

  // PKCE RFC 7636 specifies a minimum length of 43 and maximum of 128.
  // We'll ensure it's at least 43 characters long by taking a slice if it's too short
  // (though with 128 random bytes, it will almost certainly be 128 characters).
  // If you want a specific length, adjust the loop or slice.
  // For simplicity and adherence to the max length, we'll keep it at 128 here.
  return codeVerifier;
}

/**
 * Hashes a string using SHA-256 and then Base64Url encodes the result.
 * This creates the code_challenge from the code_verifier.
 *
 * @param {string} codeVerifier The code_verifier string.
 * @returns {Promise<string>} A promise that resolves to the Base64Url encoded SHA-256 hash (code_challenge).
 */
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  // Encode the code_verifier string as a Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);

  // Hash the data using SHA-256
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert the ArrayBuffer to a Base64 string
  const base64 = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));

  // Convert Base64 to Base64Url (remove padding, replace + with -, / with _)
  const base64Url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // Remove padding

  return base64Url;
}

export const buildHostedUiUrl = async (action: 'login' | 'logout' | 'settings', identityProvider: string = 'Microsft') => {
  const base = `${AWS_COGNITO_DOMAIN}`;
  const clientId = AWS_COGNITO_CLIENT_ID;
  const redirectUri = `${APP_URL}/callback`;
  const logoutUri = APP_URL;
  const responseType = 'code';
  const scope = 'openid profile email';

  // Generate the code_verifier
  const codeVerifier = generateCodeVerifier();

  // Store the code_verifier in session storage for later retrieval
  //    when the user is redirected back after authentication.
  sessionStorage.setItem(PKCE_CODE_VERIFIER_KEY, codeVerifier);

  // Construct the authorization URL for the Cognito Hosted UI
  if (action === 'login') {
    // Generate the code_challenge from the code_verifier
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Prepare URL parameters
    const params: Record<string, string> = {
      response_type: responseType,
      client_id: clientId,
      redirect_uri: redirectUri,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256', // Always S256 for Cognito
      scope,
      // state: 'optional_random_state_for_csrf_protection', // Optional, but recommended for additional CSRF
    };

    if (identityProvider) {
      params.identity_provider = encodeURIComponent(identityProvider);
    }

    return `${base}/oauth2/authorize?` + new URLSearchParams(params).toString();
  }

  if (action === 'logout') {
    // For logout, we can redirect to the logout endpoint
    return (
      `${base}/logout?` +
      new URLSearchParams({
        client_id: clientId,
        logout_uri: logoutUri,
        // state: 'optional_random_state_for_csrf_protection', // Optional, but recommended for additional CSRF
      }).toString()
    );
  }

  if (action === 'settings') {
    return `${base}/oauth2/authorize?response_type=${responseType}&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&prompt=login`;
  }

  return '';
};
